<script setup lang="ts">
import { filterStoresByScope } from '@/utils/audienceStoreIds'
import ImportSettingsModal from './ImportSettingsModal.vue'
import SelectOptin from './SelectOptin.vue'

import type { CustomerImport, ImportValueMapping } from '@/types/types'

const props = withDefaults(
  defineProps<{
    availableColumnTypes: { label: string; value: string }[]
    choices: Record<'store' | 'optin' | 'has_optin_email', { label: string; value: string }[]> | null
    isLoaded: boolean
    isLoadingStoreValues: boolean
    getChoices: () => Promise<void>
    scopedStoreIds?: number[]
  }>(),
  {
    scopedStoreIds: () => []
  }
)

const customerImport = defineModel<CustomerImport>('customerImport')
const defaultStoreId = defineModel<string | undefined>('defaultStoreId')
const defaultOptinSMS = defineModel<string | undefined>('defaultOptinSMS')
const defaultOptinEmail = defineModel<string | undefined>('defaultOptinEmail')
const savedMapping = defineModel<ImportValueMapping[] | null>('savedMapping')
const updatePointsOnExisting = defineModel<boolean>('updatePointsOnExisting', { default: false })

const { t } = useI18n()

const { send } = useEvent()
const { stores } = useLocalStorage()

const storeOptions = computed(() =>
  filterStoresByScope(stores.value, props.scopedStoreIds, (store) => store.id).map((store) => ({
    value: String(store.id),
    label: store.name
  }))
)

const columnTypeOptions = computed(() =>
  props.availableColumnTypes.map((type) => ({
    value: type.value,
    label: t(`pages.import.importPage.mapping.${type.value}`)
  }))
)

const showSettingsModal = ref(false)
const modalType = ref<'store' | 'optin' | 'has_optin_email'>()
const modalFileColumn = ref('')
const modalPongoColumn = ref('')

const tableColumns = ref({
  fileProperty: {
    label: t('pages.import.importPage.mapping.fileProperty'),
    headerClass: 'w-[32%] whitespace-normal'
  },
  status: {
    label: '',
    headerClass: 'w-14'
  },
  property: {
    label: t('pages.import.importPage.mapping.property'),
    headerClass: 'w-[44%] min-w-0 whitespace-normal'
  },
  action: {
    label: t('pages.import.importPage.mapping.action'),
    headerClass: 'w-[12%] whitespace-normal'
  }
})

const isLoaded = computed(() => {
  return props.isLoaded && customerImport.value?.columns && props.availableColumnTypes !== undefined
})

const tableRows = computed(() => {
  return (
    customerImport.value?.columns?.map((column) => {
      return {
        id: column.index,
        data: {
          fileProperty: column.header,
          uniqueIndex: column.index,
          status: getColumnStatus(column.type, column.index),
          property: column.type,
          rawFirstValues: column.rawFirstValues?.filter((value) => value !== null && value !== '')?.join(', ')
        }
      }
    }) ?? []
  )
})

const hasStoreColumn = computed(() => {
  return tableRows.value?.some((column) => column.data.property === 'store')
})

const hasOptinSMSColumn = computed(() => {
  return tableRows.value?.some((column) => column.data.property === 'optin')
})

const hasOptinEmailColumn = computed(() => {
  return tableRows.value?.some((column) => column.data.property === 'has_optin_email')
})

const getColumnStatus = (type: string, uniqueIndex: number) => {
  if (type === 'ignore') return 'ignore'

  const existingColumn = tableRows.value?.some(
    (col) => col.data.uniqueIndex !== uniqueIndex && col.data.property === type
  )
  if (existingColumn) return 'duplicate'

  if (['store', 'optin', 'has_optin_email'].includes(type)) {
    const isVerified = savedMapping.value?.some((mapping) => mapping.type === type)
    return isVerified ? 'verified' : 'unverified'
  }

  return 'matched'
}

const VALUE_MAPPING_TYPES = ['store', 'optin', 'has_optin_email'] as const

const updateColumnType = (newData: {
  property: string
  fileProperty: string
  uniqueIndex: number
  status: string
  action: string
}) => {
  let previousType: string | null = null

  const customerImportColumns = customerImport.value.columns?.map((col) => {
    if (col.index === newData.uniqueIndex) {
      previousType = col.type

      send('segment', 'customer_import.create.mapping.update', {
        customerImportId: customerImport.value?.id,
        oldValue: col.type,
        newValue: newData.property
      })

      if (previousType !== newData.property) {
        col.type = newData.property
        col.values = []

        if (
          VALUE_MAPPING_TYPES.includes(previousType as (typeof VALUE_MAPPING_TYPES)[number]) ||
          VALUE_MAPPING_TYPES.includes(newData.property as (typeof VALUE_MAPPING_TYPES)[number])
        ) {
          savedMapping.value =
            savedMapping.value?.filter(
              (mapping) => mapping.type !== previousType && mapping.type !== newData.property
            ) ?? null
        }
      }
    }
    return col
  })

  customerImport.value = { ...customerImport.value, columns: customerImportColumns }
}

const closeModal = () => {
  showSettingsModal.value = false
}

const openSettingsModal = async ({
  type,
  fileColumn,
  pongoColumn
}: {
  type: 'store' | 'optin' | 'has_optin_email'
  fileColumn: string
  pongoColumn: string
}) => {
  modalType.value = type
  modalFileColumn.value = fileColumn
  modalPongoColumn.value = pongoColumn
  showSettingsModal.value = true

  const mappedColumn = customerImport.value.columns?.find((column) => column.type === type)
  const needsColumnValues = !mappedColumn?.values?.length
  const needsStoreChoices = type === 'store' && !props.choices?.store?.length

  if (needsColumnValues || needsStoreChoices) {
    await props.getChoices()
  }
}

const handleDataLoaded = () => {
  defaultOptinSMS.value = tableRows.value?.some((column) => column.data.property === 'optin')
    ? defaultOptinSMS.value
    : 'null'
  defaultOptinEmail.value = tableRows.value?.some((column) => column.data.property === 'has_optin_email')
    ? defaultOptinEmail.value
    : 'null'
}

watch(tableRows, (newTableRows) => {
  // Store
  if (newTableRows.some((column) => column.data.property === 'store')) {
    defaultStoreId.value = undefined
  }

  // Optin
  defaultOptinSMS.value = newTableRows.some((column) => column.data.property === 'optin')
    ? undefined
    : (defaultOptinSMS.value ?? 'null')

  // Optin Email
  defaultOptinEmail.value = newTableRows.some((column) => column.data.property === 'has_optin_email')
    ? undefined
    : (defaultOptinEmail.value ?? 'null')

  if (!newTableRows.some((column) => column.data.property === 'points')) {
    updatePointsOnExisting.value = false
  }
})

watch(isLoaded, (newValue) => {
  if (newValue) handleDataLoaded()
})

onMounted(() => {
  if (isLoaded.value) handleDataLoaded()
})
</script>
<template>
  <div>
    <h2 class="text-lg font-medium">{{ t('pages.import.importPage.mapping.title') }}</h2>
    <div class="mb-6 text-sm text-muted-foreground">
      {{ t('pages.import.importPage.mapping.subtitle') }}
    </div>

    <div v-if="!isLoaded" class="mb-8">
      <table class="flex w-full flex-col gap-4 overflow-hidden rounded-lg border bg-white">
        <thead class="border-b bg-muted">
          <tr>
            <th
              scope="col"
              class="w-[32%] p-4 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {{ t('pages.import.importPage.mapping.fileProperty') }}
            </th>
            <th
              scope="col"
              class="w-14 p-4 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
            ></th>
            <th
              scope="col"
              class="w-[44%] p-4 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {{ t('pages.import.importPage.mapping.property') }}
            </th>
            <th
              scope="col"
              class="w-[12%] p-4 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {{ t('pages.import.importPage.mapping.action') }}
            </th>
          </tr>
        </thead>
        <AppSkeleton type="list" />
      </table>
    </div>

    <div
      v-if="isLoaded && (!hasStoreColumn || !hasOptinSMSColumn || !hasOptinEmailColumn)"
      class="mb-8 flex flex-col gap-6 rounded-xl border border-blue-500 bg-blue-50 p-4"
    >
      <div v-if="!hasStoreColumn" class="flex flex-col text-left text-sm">
        <div class="mb-4 flex w-full items-center text-left font-medium text-blue-600">
          <BaseIcon name="HeroBuildingOfficeIconOutline" class="mr-2 h-6 w-6" />
          {{ t('pages.import.importPage.mapping.noStore') }}
        </div>

        <label class="mb-2 w-full text-left text-foreground">
          {{ t('pages.import.importPage.mapping.noStoreLabel') }}
        </label>

        <ComboboxSelect
          v-model="defaultStoreId"
          :options="storeOptions"
          :placeholder="t('pages.import.importPage.mapping.chooseStore')"
          :search-placeholder="t('pages.import.importPage.mapping.chooseStore')"
          @update:model-value="
            (v) =>
              send('segment', 'customer_import.create.mapping.default.store', {
                customerImportId: customerImport?.id,
                value: v
              })
          "
        />
      </div>

      <div v-if="!hasOptinSMSColumn" class="flex flex-col text-left text-sm">
        <div class="mb-4 flex w-full items-center text-left font-medium text-blue-600">
          <BaseIcon name="HeroChatBubbleLeftRightIconOutline" class="mr-2 h-6 w-6" />
          {{ t('pages.import.importPage.mapping.noOptinSMS') }}
        </div>

        <label class="mb-2 w-full text-left text-foreground">
          {{ t('pages.import.importPage.mapping.noOptinSMSLabel') }}
        </label>

        <SelectOptin
          v-model="defaultOptinSMS"
          type="optin"
          @change="
            (e) =>
              send('segment', 'customer_import.create.mapping.default.optin_sms', {
                customerImportId: customerImport?.id,
                value: e.value
              })
          "
        />
      </div>

      <div v-if="!hasOptinEmailColumn" class="flex flex-col text-left text-sm">
        <div class="mb-4 flex w-full items-center text-left font-medium text-blue-600">
          <BaseIcon name="HeroChatBubbleLeftRightIconOutline" class="mr-2 h-6 w-6" />
          {{ t('pages.import.importPage.mapping.noHasOptinEmail') }}
        </div>

        <label class="mb-2 w-full text-left text-foreground">
          {{ t('pages.import.importPage.mapping.noHasOptinEmailLabel') }}
        </label>

        <SelectOptin
          v-model="defaultOptinEmail"
          type="has_optin_email"
          @change="
            (e) =>
              send('segment', 'customer_import.create.mapping.default.optin_mail', {
                customerImportId: customerImport?.id,
                value: e.value
              })
          "
        />
      </div>
    </div>

    <AppTable
      v-if="isLoaded && tableRows.length > 0"
      class="import-columns-mapping-table"
      :columns="tableColumns"
      :rows="tableRows"
      cell-classes="min-w-0 align-top"
    >
      <template #td-fileProperty="{ data }">
        <div class="flex min-w-0 flex-col whitespace-normal break-words">
          <div class="text-sm font-medium text-foreground">
            {{ data.fileProperty }}
          </div>
          <div class="text-sm text-muted-foreground">{{ data.rawFirstValues }}</div>
        </div>
      </template>

      <template #td-status="{ data }">
        <BaseIcon
          v-if="['matched', 'verified'].includes(data.status)"
          name="HeroArrowRightIconSolid"
          class="h-6 w-6 text-green-500"
        />
        <BaseIcon
          v-if="data.status === 'unverified'"
          name="HeroExclamationCircleIconSolid"
          class="h-6 w-6 text-yellow-400"
        />
        <BaseIcon
          v-if="data.status === 'ignore'"
          name="HeroEyeSlashIconSolid"
          class="h-6 w-6 text-foreground-disabled"
        />
        <BaseIcon
          v-if="data.status === 'duplicate'"
          name="HeroExclamationCircleIconSolid"
          class="h-6 w-6 text-red-500"
        />
      </template>

      <template #td-property="{ data }">
        <div class="flex w-full min-w-0 flex-col justify-center gap-2">
          <ComboboxSelect
            v-model="data.property"
            :options="columnTypeOptions"
            :trigger-class="data.status === 'duplicate' ? 'border-red-500!' : ''"
            @update:model-value="(v) => updateColumnType({ ...data, property: v })"
          >
            <template #option="{ option, selected }">
              <div class="flex flex-row items-center gap-2">
                <BaseIcon
                  v-if="option.value === 'ignore'"
                  name="HeroEyeSlashIconSolid"
                  class="mr-1 h-5 w-5 text-foreground-disabled"
                />
                <span class="text-sm text-foreground" :class="selected && 'font-semibold text-primary'">
                  {{ option.label }}
                </span>
              </div>
            </template>
          </ComboboxSelect>

          <div v-if="data.property === 'points'" class="flex w-full min-w-0 flex-col gap-1.5">
            <label for="import-update-points-on-existing" class="flex min-w-0 cursor-pointer items-center gap-2">
              <Switch
                id="import-update-points-on-existing"
                v-model="updatePointsOnExisting"
                class="shrink-0"
                @update:model-value="
                  (v) =>
                    send('segment', 'customer_import.create.mapping.update_points_on_existing', {
                      customerImportId: customerImport?.id,
                      value: v
                    })
                "
              />
              <span class="min-w-0 text-xs font-medium leading-snug text-foreground">
                {{ t('pages.import.importPage.mapping.updatePointsOnExisting.label') }}
              </span>
            </label>
            <p class="whitespace-normal break-words text-xs leading-snug text-muted-foreground">
              {{ t('pages.import.importPage.mapping.updatePointsOnExisting.description') }}
            </p>
          </div>

          <div
            v-if="data.status === 'unverified'"
            class="w-full whitespace-normal break-words text-xs text-muted-foreground"
          >
            <span v-if="data.property === 'store'">{{ t('pages.import.importPage.mapping.verifyStores') }}</span>
            <span v-else>{{ t('pages.import.importPage.mapping.verifyEmptyCells') }}</span>
          </div>

          <div v-if="data.status === 'duplicate'" class="w-full whitespace-normal break-words text-xs text-red-500">
            {{ t('pages.import.importPage.mapping.duplicate') }}
          </div>
        </div>
      </template>

      <template #td-action="{ data }">
        <Button
          v-if="['verified', 'unverified'].includes(data.status)"
          variant="outline"
          class="w-full whitespace-normal py-2! pl-3! pr-2!"
          @click="
            openSettingsModal({
              type: data.property,
              fileColumn: data.fileProperty,
              pongoColumn: t(`pages.import.importPage.mapping.${data.property}`)
            })
          "
        >
          <div class="flex flex-row items-center justify-center gap-2 text-foreground">
            {{ t('pages.import.importPage.mapping.verifyButton') }}
            <BaseIcon name="HeroDocumentMagnifyingGlassIconOutline" class="h-5 w-5 shrink-0" />
          </div>
        </Button>
      </template>
    </AppTable>
  </div>

  <ImportSettingsModal
    v-if="showSettingsModal"
    v-model:saved-mapping="savedMapping"
    :customer-import="customerImport"
    :choices="choices"
    :type="modalType"
    :file-column="modalFileColumn"
    :pongo-column="modalPongoColumn"
    :is-loading-store-values="isLoadingStoreValues"
    :reload-choices="getChoices"
    @close-modal="closeModal"
  />
</template>

<style scoped>
.import-columns-mapping-table :deep([data-slot='table']) {
  width: 100% !important;
  max-width: 100%;
  min-width: 0 !important;
  table-layout: fixed;
}

.import-columns-mapping-table :deep([data-slot='table'] th),
.import-columns-mapping-table :deep([data-slot='table'] td) {
  overflow-wrap: break-word;
}
</style>
